/**
 * Colour helpers, lifted from infostation.html unchanged.
 *
 * Every node's border, fill and text colour is derived from one stored hex, differently per
 * theme, so these three run on every node on every render. They are pure string maths and
 * exist here so the map components never reach for a global.
 */

/** `#rrggbb` + alpha -> `rgba(...)`. Node borders and fills. */
export function hexA(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}

/**
 * Lighten (`p > 0`) or darken (`p < 0`) a hex colour, as `rgb(...)`.
 *
 * Used for node text in the light theme, where the stored colour is too saturated to read
 * against a pale fill. The asymmetry is deliberate: darkening scales towards 0, lightening
 * scales the REMAINING distance to 255, so ±p are not inverses of each other.
 */
export function shade(hex: string, p: number): string {
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  const f = (t: number) => Math.round(t + (p < 0 ? t * p : (255 - t) * p));
  return `rgb(${f(r)},${f(g)},${f(b)})`;
}

/** The app's default node/tag colour, used whenever a stored one is missing or malformed. */
export const DEFAULT_COLOR = '#5b8cff';

/**
 * The branch colours: what a new top-level branch is painted with (cycled as nodes are
 * created), and — since V0.54 — the swatches the right-click menu's Colour offers for a node
 * or a dotted link. One list, so a colour picked from the menu is always one the map itself
 * would have chosen.
 */
export const BRANCH_COLORS = [
  '#5b8cff', '#1fb6a8', '#f0663f', '#e05a9c', '#8b6bff',
  '#3aa655', '#e0982a', '#e0574f', '#2bb0d8', '#c56bd8',
] as const;

/**
 * Coerce anything to a `#rrggbb` string.
 *
 * `<input type="color">` accepts nothing else — not a name, not a shorthand, not an
 * `rgb()` — and silently shows black for a value it cannot parse, so the fallback matters.
 */
export function toHex6(c: unknown): string {
  return typeof c === 'string' && /^#[0-9a-fA-F]{6}$/.test(c) ? c : DEFAULT_COLOR;
}

/** The root gradient's two stops — for the one surface (the minimap's SVG, V0.53.4) that cannot
 *  take a CSS gradient string and has to declare the stops itself. */
export const ROOT_GRADIENT_STOPS = ['#5b8cff', '#a06bff'] as const;

/** The root node's paint: a gradient, no border, white text — the same on every surface. */
export const ROOT_GRADIENT = `linear-gradient(135deg,${ROOT_GRADIENT_STOPS[0]},${ROOT_GRADIENT_STOPS[1]})`;

/** The three colours a node-tinted box derives from one stored hex, per theme. */
export interface NodeTint {
  borderColor: string;
  background: string;
  color: string;
}

/**
 * How a node is painted from its colour — THE ONE FORMULA (V0.47).
 *
 * A node on the map, its bubble in the document view's band, the chip in the dashboard tables
 * and the row in the search results all tint a box from the node's stored colour: border at
 * .5/.55 alpha, fill at .09/.16, text darkened in the light theme so a saturated hue stays
 * legible on a pale fill. Four files carried that formula as four copies, and the node's
 * flight between those surfaces (V0.47) is a fifth consumer — a clone that has to match all of
 * them exactly or it visibly changes shade as it lands. So the formula lives here once.
 *
 * `toHex6` on the way in: one caller was passing `'var(--accent)'`, which `hexA` cannot parse,
 * and painting `rgba(NaN,NaN,NaN,…)` without anything noticing.
 */
export function nodeTint(color: unknown, isLight: boolean): NodeTint {
  const c = toHex6(color);
  return {
    borderColor: hexA(c, isLight ? 0.5 : 0.55),
    background: hexA(c, isLight ? 0.09 : 0.16),
    color: isLight ? shade(c, -0.35) : 'var(--ink)',
  };
}
